/**
 * Adapted from PlayCanvas Engine's gsplat-resource-base.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'
import { GSPLAT_INSTANCE_SIZE } from '@/plugins/gsplat/core/gsplatBatching'
import type { GSplatResourceLease } from '@/plugins/gsplat/GSplatHandleBinding'
import type {
  GSplatBufferLeaf,
  GSplatGraphicsAdapter,
  GSplatMaterialLeaf,
  GSplatTextureDimensions,
  GSplatTextureLeaf,
  GSplatWorkBufferSourcePipelineLeaf,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatFormat, GSplatStreamDescriptor } from '@/plugins/gsplat/lod/GSplatFormat'
import { GSplatStreams } from '@/plugins/gsplat/lod/GSplatStreams'
import { GSplatResourceCleanup } from '@/plugins/gsplat/unified/resource/GSplatResourceCleanup'
import type { Disposable } from '@/shared/types/core'

let nextResourceId = 0

const WORK_BUFFER_VERTEX_SOURCE = '#include "gsplatCopyInstancedQuadVS"'
const WORK_BUFFER_FRAGMENT_SOURCE = '#include "gsplatCopyToWorkbufferPS"'

export type GSplatResourceKind = 'uncompressed' | 'compressed' | 'sog' | 'container'

export interface GSplatWorkBufferModifierSource {
  readonly code: string
  readonly hash: number
}

export interface GSplatResourceMesh extends Disposable {
  readonly positions: Float32Array
  readonly indices: Uint32Array
  readonly vertexBuffer: GSplatBufferLeaf
  readonly indexBuffer: GSplatBufferLeaf
  readonly aabb: THREE.Box3
}

/** Resource cache 中与 PlayCanvas WorkBufferRenderInfo 对应的 adapter-neutral owner。 */
export class GSplatWorkBufferRenderInfo implements Disposable {
  private destroyed = false

  constructor(
    readonly key: string,
    readonly material: GSplatMaterialLeaf,
    readonly pipeline: GSplatWorkBufferSourcePipelineLeaf,
    readonly colorOnly: boolean,
    readonly format: GSplatFormat
  ) {
    if (pipeline.key !== key) {
      throw new Error(`GSplat WorkBuffer source pipeline key 不匹配: ${pipeline.key}`)
    }
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.pipeline.dispose()
    this.material.dispose()
  }

  dispose(): void {
    this.destroy()
  }
}

/** Resource 保留格式原生 CPU data，不要求统一展开为 SplatData。 */
export interface GSplatResourceDataSource {
  readonly numSplats: number
  getCenters?(): Float32Array | null
  calcAabb(target: THREE.Box3): void
}

export interface GSplatResourceBaseOptions<TData extends GSplatResourceDataSource, TIterator> {
  readonly adapter: GSplatGraphicsAdapter
  readonly data: TData
  readonly format: GSplatFormat
  readonly prepareCenters?: boolean
  readonly generation?: number
  readonly parameters?: ReadonlyMap<string, unknown>
  readonly defines?: ReadonlyMap<string, unknown>
  readonly createIterator?: (data: TData) => TIterator
  readonly getCpuExportData?: (data: TData) => unknown | null
  /** 可选 backend-neutral CPU 椭球读取器；radii 必须已经是实际半径而非 log scale。 */
  readonly readSplatShape?: (
    index: number,
    position: THREE.Vector3,
    rotation: THREE.Quaternion,
    radii: THREE.Vector3
  ) => boolean
  /**
   * 可选 backend-neutral CPU 基色读取器；color.rgb 必须是 shader readBaseColor 等价量
   * （0.5 + SH0 * SH_C0），alpha 为激活后不透明度。
   */
  readonly readSplatColor?: (index: number, color: THREE.Vector4) => boolean
  readonly disposeData?: (data: TData) => void
}

/**
 * Unified Resource family 的唯一基类。
 *
 * 领域层拥有 CPU data、centers/bounds/version/refcount；GPU 叶子只能通过 adapter-backed
 * GSplatStreams 租约挂入，不能在 Resource 内按 backend 创建另一套对象模型。
 */
export abstract class GSplatResourceBase<
  TData extends GSplatResourceDataSource,
  TIterator = unknown,
> implements Disposable
{
  readonly id = nextResourceId++
  readonly generation: number
  readonly adapter: GSplatGraphicsAdapter
  readonly data: TData
  readonly format: GSplatFormat
  readonly aabb: THREE.Box3
  readonly parameters: ReadonlyMap<string, unknown>
  readonly defines: ReadonlyMap<string, unknown>
  readonly object3D = null
  centersVersion = 0
  contentRevision = 0

  abstract readonly kind: GSplatResourceKind

  private centerData: Float32Array | null
  private ownedStreams: GSplatStreams | null
  private refCountValue = 0
  private meshRefCountValue = 0
  private meshValue: GSplatResourceMesh | null = null
  private readonly workBufferRenderInfos = new Map<string, GSplatWorkBufferRenderInfo>()
  private pendingDestroy = false
  private destroyed = false
  private readonly createIteratorCallback: ((data: TData) => TIterator) | null
  private readonly cpuExportCallback: ((data: TData) => unknown | null) | null
  private readonly readSplatShapeCallback:
    | ((
        index: number,
        position: THREE.Vector3,
        rotation: THREE.Quaternion,
        radii: THREE.Vector3
      ) => boolean)
    | null
  private readonly readSplatColorCallback: ((index: number, color: THREE.Vector4) => boolean) | null
  private readonly disposeDataCallback: ((data: TData) => void) | null
  private readonly streamRevisions = new Map<string, number>()

  protected constructor(options: GSplatResourceBaseOptions<TData, TIterator>) {
    if (!Number.isSafeInteger(options.data.numSplats) || options.data.numSplats < 0) {
      throw new Error(`非法 GSplat Resource splat count: ${options.data.numSplats}`)
    }
    if (options.format.graphicsAdapter !== options.adapter) {
      throw new Error('GSplat Resource format 与 adapter 不属于同一 graphics device')
    }
    this.adapter = options.adapter
    this.data = options.data
    this.format = options.format
    this.generation = options.generation ?? this.id + 1
    this.parameters = options.parameters ?? new Map()
    this.defines = options.defines ?? new Map()
    this.createIteratorCallback = options.createIterator ?? null
    this.cpuExportCallback = options.getCpuExportData ?? null
    this.readSplatShapeCallback = options.readSplatShape ?? null
    this.readSplatColorCallback = options.readSplatColor ?? null
    this.disposeDataCallback = options.disposeData ?? null
    this.ownedStreams = new GSplatStreams(options.adapter)
    this.centerData =
      options.prepareCenters === false ? null : (options.data.getCenters?.() ?? null)
    this.validateCenters(this.centerData)
    this.aabb = new THREE.Box3()
    options.data.calcAabb(this.aabb)
  }

  get numSplats(): number {
    return this.data.numSplats
  }

  /** 当前 Resource GPU leaves 所属的唯一 adapter。 */
  get graphicsAdapter(): GSplatGraphicsAdapter {
    return this.adapter
  }

  get centers(): Float32Array | null {
    return this.centerData
  }

  set centers(value: Float32Array | null) {
    this.assertActive()
    this.validateCenters(value)
    this.centerData = value
  }

  get hasCenters(): boolean {
    return this.centerData !== null
  }

  get boundingBox(): THREE.Box3 {
    return this.aabb
  }

  get streams(): GSplatStreams | null {
    return this.ownedStreams
  }

  get textureDimensions(): GSplatTextureDimensions {
    return this.ownedStreams?.textureDimensions ?? { width: 0, height: 0 }
  }

  /** 跨 View 只计一次的 backend source GPU leaf 字节数。 */
  get sourceGpuBytes(): number {
    let total = 0
    for (const texture of this.ownedStreams?.getTexturesInOrder() ?? []) {
      const bytesPerTexel = this.adapter.getTextureFormatInfo(texture.format).bytesPerTexel
      const byteLength = texture.width * texture.height * bytesPerTexel
      if (!Number.isSafeInteger(byteLength) || byteLength < 0) {
        throw new Error(`非法 GSplat source texture byteLength: ${texture.label}`)
      }
      total += byteLength
      if (!Number.isSafeInteger(total)) throw new Error('GSplat source GPU bytes 超出安全整数范围')
    }
    return total
  }

  get refCount(): number {
    return this.refCountValue
  }

  get meshRefCount(): number {
    return this.meshRefCountValue
  }

  get mesh(): GSplatResourceMesh | null {
    return this.meshValue
  }

  get canFinalizeDestroy(): boolean {
    return this.refCountValue === 0 && this.meshRefCountValue === 0
  }

  get isPendingDestroy(): boolean {
    return this.pendingDestroy
  }

  get isDestroyed(): boolean {
    return this.destroyed
  }

  get supportsWorkBufferGeometry(): boolean {
    return false
  }

  /** Loader 完成 GPU staging 后一次性交移当前 format 的 texture owner。 */
  attachStreams(streams: GSplatStreams): void {
    this.assertActive()
    if (this.pendingDestroy) throw new Error('GSplat Resource 已进入 retire，不能挂接 GPU streams')
    if (this.ownedStreams?.format) throw new Error('GSplat Resource 只能挂接一份 GPU streams')
    if (streams.graphicsAdapter !== this.adapter) {
      throw new Error('GSplat Resource streams 与 adapter 不属于同一 graphics device')
    }
    if (streams.format !== this.format) {
      throw new Error('GSplat Resource streams 必须已绑定当前 format')
    }
    this.ownedStreams?.destroy()
    this.ownedStreams = streams
  }

  getTexture(name: string): GSplatTextureLeaf | null {
    this.assertActive()
    return this.ownedStreams?.getTexture(name) ?? null
  }

  configureMaterialDefines(defines: Map<string, unknown>): void {
    this.assertActive()
    for (const [name, value] of this.defines) defines.set(name, value)
  }

  /**
   * 按冻结上游顺序配置 Resource source material：define → stream sync → chunk → texture → parameter。
   */
  configureMaterial(
    material: GSplatMaterialLeaf,
    workBufferModifier: GSplatWorkBufferModifierSource | null,
    formatDeclarations: string
  ): void {
    this.assertActive()
    const resourceDefines = new Map<string, unknown>()
    this.configureMaterialDefines(resourceDefines)
    for (const [name, value] of resourceDefines) material.setDefine(name, value)
    const streams = this.ownedStreams
    if (!streams) throw new Error('GSplat Resource 尚未挂接 GPU streams')
    streams.syncWithFormat(this.format)

    const chunks = material.getShaderChunks(material.language)
    chunks.set('gsplatDeclarationsVS', formatDeclarations)
    chunks.set('gsplatReadVS', this.format.getReadCode(material.language))
    if (workBufferModifier?.code) chunks.set('gsplatModifyVS', workBufferModifier.code)

    for (const stream of this.format.resourceStreams) {
      const texture = streams.getTexture(stream.name)
      if (!texture) throw new Error(`GSplat Resource texture 缺失: ${stream.name}`)
      material.setParameter(stream.name, texture)
    }
    for (const [name, value] of this.parameters) material.setParameter(name, value)
    if (this.textureDimensions.width > 0) {
      material.setParameter('splatTextureSize', this.textureDimensions.width)
    }
  }

  getWorkBufferRenderInfo(
    colorOnly: boolean,
    workBufferModifier: GSplatWorkBufferModifierSource | null,
    formatHash: number,
    formatDeclarations: string,
    workBufferFormat: GSplatFormat
  ): GSplatWorkBufferRenderInfo {
    this.assertActive()
    if (!Number.isSafeInteger(formatHash)) throw new Error(`非法 GSplat format hash: ${formatHash}`)

    // operation-local，异常或重入都不能污染另一 Resource。
    const defines = new Map<string, unknown>()
    this.configureMaterialDefines(defines)
    if (!defines.has('SH_BANDS')) defines.set('SH_BANDS', '0')
    defines.set('GSPLAT_LOD', '')
    if (colorOnly) {
      defines.set('GSPLAT_COLOR_ONLY', '')
      if (this.supportsWorkBufferGeometry) {
        defines.set('GSPLAT_WORKBUFFER_GEOMETRY', '')
        if (workBufferFormat.dataFormat === 'compact') {
          defines.set('GSPLAT_WORKBUFFER_COMPACT', '')
        }
      }
    }
    const outputStreams = this.resolveWorkBufferOutputs(colorOnly, workBufferFormat)
    const key = createWorkBufferRenderInfoKey({
      formatHash,
      formatDeclarations,
      sourceRead: this.format.getReadCode(this.adapter.language),
      workBufferFormat,
      workBufferModifier,
      defines,
      colorOnly,
    })
    const cached = this.workBufferRenderInfos.get(key)
    if (cached) return cached

    let material: GSplatMaterialLeaf | null = null
    let pipeline: GSplatWorkBufferSourcePipelineLeaf | null = null
    try {
      material = this.adapter.createMaterial({
        label: `gsplat-work-buffer-source:${this.id}`,
        language: this.adapter.language,
        vertexSource: WORK_BUFFER_VERTEX_SOURCE,
        fragmentSource: WORK_BUFFER_FRAGMENT_SOURCE,
      })
      // PlayCanvas GSplatWorkBufferRenderPass resets to the default draw state before copying.
      // WorkBuffer attachments store encoded transform/color data and must be overwritten exactly;
      // inheriting the transparent splat material's premultiplied-alpha state corrupts every stream.
      material.setRasterState({
        cullMode: 'none',
        blendMode: 'none',
        depthTest: false,
        depthWrite: false,
      })
      this.configureMaterial(material, workBufferModifier, formatDeclarations)

      const chunks = material.getShaderChunks(this.adapter.language)
      let outputCode = workBufferFormat.getOutputDeclarations(outputStreams, this.adapter.language)
      const outputNames = new Set(outputStreams.map((stream) => stream.name))
      const stubStreams = workBufferFormat.extraStreams.filter(
        (stream) => !outputNames.has(stream.name)
      )
      if (stubStreams.length > 0) {
        outputCode += `\n${workBufferFormat.getOutputStubs(stubStreams, this.adapter.language)}`
      }
      if (outputCode.includes('writeTreeBind')) {
        outputCode = `#define GSPLAT_HAS_TREE_BIND\n${outputCode}`
      }
      chunks.set('gsplatWorkBufferOutputVS', outputCode)
      const writeCode = workBufferFormat.getWriteCode(this.adapter.language)
      if (writeCode) chunks.set('gsplatWriteVS', writeCode)
      for (const [name, value] of defines) material.setDefine(name, value)
      material.update()

      pipeline = this.adapter.createPrivateWorkBufferSourcePipeline({
        label: `gsplat-work-buffer-source:${this.id}`,
        key,
        language: this.adapter.language,
        material,
        colorOnly,
        instanced: true,
        outputTypes: Object.freeze(
          outputStreams.map(
            (stream) => this.adapter.getTextureFormatInfo(stream.format).shaderValueType
          )
        ),
      })
      const info = new GSplatWorkBufferRenderInfo(
        key,
        material,
        pipeline,
        colorOnly,
        workBufferFormat
      )
      this.workBufferRenderInfos.set(key, info)
      return info
    } catch (error) {
      pipeline?.dispose()
      material?.dispose()
      throw error
    }
  }

  ensureMesh(): GSplatResourceMesh {
    this.assertActive()
    if (!this.meshValue) this.meshValue = this.createMesh()
    this.meshRefCountValue++
    return this.meshValue
  }

  releaseMesh(): void {
    if (this.meshRefCountValue === 0) throw new Error('GSplat Resource mesh refCount underflow')
    this.meshRefCountValue--
    if (this.meshRefCountValue === 0) {
      this.meshValue?.dispose()
      this.meshValue = null
    }
  }

  markStreamsWritten(streamNames: readonly string[]): void {
    this.assertActive()
    if (streamNames.length === 0) return
    const unique = new Set(streamNames)
    for (const name of unique) {
      if (!this.format.getStream(name)) throw new Error(`GSplat stream 未声明: ${name}`)
    }
    this.contentRevision++
    for (const name of unique) {
      this.streamRevisions.set(name, (this.streamRevisions.get(name) ?? 0) + 1)
    }
    if (unique.has('dataCenter')) this.centersVersion++
  }

  getStreamRevision(name: string): number {
    return this.streamRevisions.get(name) ?? 0
  }

  /** Dynamic container 等 owner 发布新 centers 时显式推进版本。 */
  replaceCenters(value: Float32Array | null): void {
    this.centers = value
    this.centersVersion++
  }

  createIterator(): TIterator | null {
    this.assertActive()
    return this.createIteratorCallback?.(this.data) ?? null
  }

  getCpuExportData(): unknown | null {
    this.assertActive()
    return this.cpuExportCallback ? this.cpuExportCallback(this.data) : this.data
  }

  readSplatShape(
    index: number,
    position: THREE.Vector3,
    rotation: THREE.Quaternion,
    radii: THREE.Vector3
  ): boolean {
    this.assertActive()
    return this.readSplatShapeCallback?.(index, position, rotation, radii) ?? false
  }

  readSplatColor(index: number, color: THREE.Vector4): boolean {
    this.assertActive()
    return this.readSplatColorCallback?.(index, color) ?? false
  }

  /** Component/System clone 使用的独立引用租约。 */
  acquire(): GSplatResourceLease {
    this.incRefCount()
    const owner = this
    let released = false
    return {
      get resourceIdentity() {
        return owner
      },
      get graphicsAdapter() {
        return owner.adapter
      },
      get numSplats() {
        return owner.numSplats
      },
      get boundingBox() {
        return owner.boundingBox
      },
      get aabb() {
        return owner.aabb
      },
      get object3D() {
        return owner.object3D
      },
      get generation() {
        return owner.generation
      },
      get format() {
        return owner.format
      },
      get resourceKind() {
        return owner.kind
      },
      get streaming() {
        return 'static' as const
      },
      get octreeSemantics() {
        return 'none' as const
      },
      get streams() {
        return owner.streams
      },
      get data() {
        return owner.data
      },
      get centers() {
        return owner.centers
      },
      get sourceGpuBytes() {
        return owner.sourceGpuBytes
      },
      get parameters() {
        return owner.parameters
      },
      get centersVersion() {
        return owner.centersVersion
      },
      createIterator: () => owner.createIterator(),
      getTexture: (name) => owner.getTexture(name),
      configureMaterialDefines: (defines) => owner.configureMaterialDefines(defines),
      markStreamsWritten: (streamNames) => owner.markStreamsWritten(streamNames),
      getStreamRevision: (name) => owner.getStreamRevision(name),
      getCpuExportData: () => owner.getCpuExportData(),
      readSplatShape: (index, position, rotation, radii) =>
        owner.readSplatShape(index, position, rotation, radii),
      acquire: () => owner.acquire(),
      dispose: () => {
        if (released) return
        released = true
        owner.decRefCount()
      },
    }
  }

  incRefCount(): void {
    this.assertActive()
    this.refCountValue++
  }

  decRefCount(): void {
    if (this.refCountValue === 0) {
      throw new Error(`GSplat Resource[${this.id}] refCount underflow`)
    }
    this.refCountValue--
  }

  destroy(): void {
    if (this.destroyed || this.pendingDestroy) return
    if (!this.canFinalizeDestroy) {
      this.pendingDestroy = true
      GSplatResourceCleanup.queueDestroy(this.adapter, this)
      return
    }
    this.actualDestroy()
  }

  dispose(): void {
    this.destroy()
  }

  protected actualDestroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.pendingDestroy = false
    this.centerData = null
    this.ownedStreams?.destroy()
    this.ownedStreams = null
    this.meshValue?.dispose()
    this.meshValue = null
    this.meshRefCountValue = 0
    for (const info of this.workBufferRenderInfos.values()) info.destroy()
    this.workBufferRenderInfos.clear()
    this.disposeDataCallback?.(this.data)
  }

  /** 仅供 GSplatResourceCleanup.process 调用。 */
  finalizeDeferredDestroy(): void {
    if (!this.pendingDestroy || !this.canFinalizeDestroy) return
    this.actualDestroy()
  }

  protected assertActive(): void {
    if (this.destroyed) throw new Error('GSplat Resource 已销毁')
  }

  private validateCenters(value: Float32Array | null): void {
    if (value && value.length !== this.data.numSplats * 3) {
      throw new Error(
        `GSplat centers 长度不匹配：actual=${value.length}, expected=${this.data.numSplats * 3}`
      )
    }
  }

  private resolveWorkBufferOutputs(
    colorOnly: boolean,
    workBufferFormat: GSplatFormat
  ): readonly GSplatStreamDescriptor[] {
    if (!colorOnly) return workBufferFormat.resourceStreams
    const color = workBufferFormat.getStream('dataColor')
    if (!color) throw new Error('GSplat WorkBuffer color-only 要求 dataColor stream')
    return Object.freeze([color])
  }

  private createMesh(): GSplatResourceMesh {
    const positions = new Float32Array(12 * GSPLAT_INSTANCE_SIZE)
    const indices = new Uint32Array(6 * GSPLAT_INSTANCE_SIZE)
    for (let index = 0; index < GSPLAT_INSTANCE_SIZE; index++) {
      positions.set([-1, -1, index, 1, -1, index, 1, 1, index, -1, 1, index], index * 12)
      const base = index * 4
      indices.set([base, base + 1, base + 2, base, base + 2, base + 3], index * 6)
    }
    if (
      positions.byteLength > this.adapter.limits.maxBufferSize ||
      indices.byteLength > this.adapter.limits.maxBufferSize
    ) {
      throw new Error('GSplat 128-splat mesh 超出 adapter buffer limit')
    }

    let vertexBuffer: GSplatBufferLeaf | null = null
    let indexBuffer: GSplatBufferLeaf | null = null
    try {
      vertexBuffer = this.adapter.createBuffer({
        label: `gsplat-resource-mesh-vertices:${this.id}`,
        byteLength: positions.byteLength,
        usages: Object.freeze(['vertex', 'copy-dst'] as const),
      })
      indexBuffer = this.adapter.createBuffer({
        label: `gsplat-resource-mesh-indices:${this.id}`,
        byteLength: indices.byteLength,
        usages: Object.freeze(['index', 'copy-dst'] as const),
      })
      this.adapter.updateBuffer(vertexBuffer, positions)
      this.adapter.updateBuffer(indexBuffer, indices)
      return new AdapterResourceMesh(
        positions,
        indices,
        vertexBuffer,
        indexBuffer,
        this.aabb.clone()
      )
    } catch (error) {
      indexBuffer?.dispose()
      vertexBuffer?.dispose()
      throw error
    }
  }
}

class AdapterResourceMesh implements GSplatResourceMesh {
  private destroyed = false

  constructor(
    readonly positions: Float32Array,
    readonly indices: Uint32Array,
    readonly vertexBuffer: GSplatBufferLeaf,
    readonly indexBuffer: GSplatBufferLeaf,
    readonly aabb: THREE.Box3
  ) {}

  dispose(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.indexBuffer.dispose()
    this.vertexBuffer.dispose()
  }
}

interface WorkBufferRenderInfoKeyInput {
  readonly formatHash: number
  readonly formatDeclarations: string
  readonly sourceRead: string
  readonly workBufferFormat: GSplatFormat
  readonly workBufferModifier: GSplatWorkBufferModifierSource | null
  readonly defines: ReadonlyMap<string, unknown>
  readonly colorOnly: boolean
}

function createWorkBufferRenderInfoKey(input: WorkBufferRenderInfoKeyInput): string {
  const allStreams = [...input.workBufferFormat.streams, ...input.workBufferFormat.extraStreams]
  return JSON.stringify({
    sourceFormatHash: input.formatHash,
    sourceDeclarations: input.formatDeclarations,
    sourceRead: input.sourceRead,
    workBufferFormatHash: input.workBufferFormat.hash,
    workBufferDataFormat: input.workBufferFormat.dataFormat,
    workBufferStreams: allStreams.map((stream) => [stream.name, stream.format, stream.storage]),
    writeGLSL: input.workBufferFormat.getWriteCode('glsl'),
    writeWGSL: input.workBufferFormat.getWriteCode('wgsl'),
    modifierHash: input.workBufferModifier?.hash ?? 0,
    modifierCode: input.workBufferModifier?.code ?? '',
    defines: [...input.defines],
    colorOnly: input.colorOnly,
  })
}
